import { useEffect, useRef, useCallback } from 'react';
import useNotificationState from '@/states/notification.state';
import useTenantState from '@/states/tenant.state';

interface UseNotificationPollingParams {
  enabled?: boolean;
  pollingInterval?: number; // in milliseconds
}

interface UseNotificationPollingReturn {
  refetch: () => void;
}

/**
 * Hook to manage notifications via REST API polling
 * Polls the notification endpoint periodically to check for new notifications
 */
export const useNotificationWebSocket = ({
  enabled = true,
  pollingInterval = 30000, // Default: 30 seconds
}: UseNotificationPollingParams = {}): UseNotificationPollingReturn => {
  const pollingRef = useRef<NodeJS.Timeout>();
  const previousUnreadCountRef = useRef<number>(0);

  const { currentTenant } = useTenantState();
  const { fetchNotifications, fetchUnreadCount, unreadCount } = useNotificationState();

  const refetch = useCallback(() => {
    if (currentTenant?.id) {
      fetchNotifications(currentTenant.id);
      fetchUnreadCount(currentTenant.id);
    }
  }, [currentTenant?.id, fetchNotifications, fetchUnreadCount]);

  // Start polling
  useEffect(() => {
    if (!enabled || !currentTenant?.id) return;

    // Initial fetch
    refetch();

    // Set up polling interval
    pollingRef.current = setInterval(() => {
      refetch();
    }, pollingInterval);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [enabled, currentTenant?.id, pollingInterval, refetch]);

  // Track unread count changes (optional: for showing toast when new notifications arrive)
  useEffect(() => {
    // Only show notification if count increased (new notification arrived)
    if (unreadCount > previousUnreadCountRef.current && previousUnreadCountRef.current > 0) {
      // New notifications arrived - the UI will update automatically via state
      console.log('[NotificationPolling] New notifications detected:', unreadCount - previousUnreadCountRef.current);
    }
    previousUnreadCountRef.current = unreadCount;
  }, [unreadCount]);

  return {
    refetch,
  };
};

export default useNotificationWebSocket;
